import { setSession } from "./session-helper";

const RILL_LOADER_NAMESPACE = "@rill/loader";
const EDIT_EXERCISES_PREFIX = `${RILL_LOADER_NAMESPACE}/editExercises/`;
// One cached exercise list this large, beside libraryCycles and the rest of
// the session, crosses Mongo's 16MB document limit.
const HUGE_EXERCISE_CACHE_BYTES = 4 * 1024 * 1024;
const EXERCISE_SAMPLE_SIZE = 5;

/**
 * Builds the cache key that @rill/loader uses internally for a given loader
 * name and its arguments. Centralised here so any change to the loader's key
 * format only needs to be updated in one place.
 */
export const loaderCacheKey = (name, ...args) =>
  `${RILL_LOADER_NAMESPACE}/${name}/${JSON.stringify(args)}`;

/**
 * Returns true if the session already holds a cached value for the given
 * loader name + args, meaning load() will resolve instantly on the next render.
 */
export const hasLoaderCache = (session, name, ...args) =>
  session.has(loaderCacheKey(name, ...args));

/**
 * Reads a cached loader value without going through load(). Undefined when
 * the key isn't in the session — does not delete it (unlike getSessionValue).
 */
export const readLoaderCache = (session, name, ...args) => {
  const key = loaderCacheKey(name, ...args);
  return session.has(key) ? session.get(key) : undefined;
};

/**
 * Writes a value into the session under the key @rill/loader uses, so that
 * the next call to load(name, ...args) returns from cache without hitting the API.
 */
export const writeLoaderCache = (session, name, value, opts = {}, ...args) => {
  const { ttl = "2 hours", meta = ["no-bust"] } = opts;
  setSession(session, loaderCacheKey(name, ...args), value, ttl, { meta, ttl });
};

/**
 * An editExercises cache entry that must not be written into the session.
 * Unscoped keys are the "every exercise for this athlete" payload. A scoped
 * list is only dropped once it is large enough to threaten the BSON limit;
 * the page already has the value on locals and will refetch next time.
 */
export const editExercisesCacheIsUnsafe = (key, value) => {
  if (!key || !String(key).startsWith(EDIT_EXERCISES_PREFIX)) return false;
  const unscoped = !/"cycle"\s*:/.test(String(key));
  if (unscoped) return !Array.isArray(value) || value.length > 0;
  if (!Array.isArray(value) || value.length < 100) return false;
  // Stringifying the whole list on every request is the cost we are avoiding,
  // so estimate from a few items instead.
  try {
    const sample = value.slice(0, EXERCISE_SAMPLE_SIZE);
    const sampleBytes = JSON.stringify(sample).length;
    return (
      (sampleBytes / sample.length) * value.length > HUGE_EXERCISE_CACHE_BYTES
    );
  } catch {
    return true;
  }
};

export const dropUnsafeEditExercises = (session) => {
  if (!session?.items) return;
  const keys = [];
  for (const item of session.items) {
    if (editExercisesCacheIsUnsafe(item?.key, item?.value)) {
      keys.push(item.key);
    }
  }
  for (const key of keys) session.delete(key);
};

/**
 * Object-spread merge into an existing loader cache entry (e.g. accumulating
 * paginated maps). Creates the key if it doesn't exist yet.
 */
export const mergeLoaderCache = (session, name, patch, opts = {}, ...args) => {
  const existing = readLoaderCache(session, name, ...args);
  const merged = {
    ...(existing && typeof existing === "object" ? existing : {}),
    ...patch,
  };
  writeLoaderCache(session, name, merged, opts, ...args);
  return merged;
};
